// A tiny app-level toast (snackbar): a transient message with an OPTIONAL action button,
// announced politely to assistive tech. One primitive serves the whole app.
//
// - Store-agnostic: callers pass a `message` and an optional `{ actionLabel, onAction }`.
//   Callers layer their own behavior on top: Undo on a favorite removal, or a sticky
//   notice (`duration: 0`) for a persistent prompt.
// - One at a time (replace, not stack): showing a new toast dismisses the previous one.
//   Trade-off: rapid removals leave only the LAST one undoable, acceptable for a
//   low-stakes re-doable action.
// - Polite live region, no focus theft: a persistent `role="status"` / `aria-live="polite"`
//   host announces without moving focus; the buttons are real `<button>`s, nothing trapped.

import { el } from "../lib/dom.js";

const REGION_ID = "toast-region";
const DEFAULT_DURATION_MS = 6000;

// Screen readers watch a live region that already exists in the DOM, so keep ONE host
// around (rather than minting a fresh element per message) for reliable announcements.
function toastRegion() {
  const existing = document.getElementById(REGION_ID);
  if (existing) {
    return existing;
  }
  const region = el("div", { id: REGION_ID, class: "toast-region", role: "status", "aria-live": "polite" });
  document.body.append(region);
  return region;
}

// Pure DOM construction; no timer lives here. Every listener registers with `listen` so
// it is released when the toast's controller aborts.
function buildToast({ message, actionLabel, onAction, dismiss }, listen) {
  const toast = el("div", { class: "app-toast" });
  toast.append(el("span", { class: "text" }, message));

  if (actionLabel && onAction) {
    const actionBtn = el("button", { type: "button", class: "pill action", "data-variant": "primary" }, actionLabel);
    const handleAction = () => {
      onAction();
      dismiss();
    };
    actionBtn.addEventListener("click", handleAction, listen);
    toast.append(actionBtn);
  }

  const closeBtn = el(
    "button",
    { type: "button", class: "icon-btn dismiss", "aria-label": "Dismiss notification" },
    "×",
  );
  closeBtn.addEventListener("click", dismiss, listen);
  toast.append(closeBtn);

  return toast;
}

// The auto-dismiss countdown, with pause-on-hover/focus. Timer and pause state are
// closure-local, so the "can it leak or stack a timer?" question is answerable here:
// `start()` always clears the pending timer before arming a fresh one, and the `abort`
// handler clears it on teardown, so no path leaves two timers running or one past teardown.
function installAutoDismiss(toast, duration, dismiss, signal) {
  const listen = { signal };
  let timer = 0;
  let hovering = false;
  let focusWithin = false;

  const start = () => {
    clearTimeout(timer);
    timer = window.setTimeout(dismiss, duration);
  };
  // Restart the FULL duration only once BOTH pause sources (hover, focus) release.
  const resume = () => {
    if (!hovering && !focusWithin) {
      start();
    }
  };
  // Teardown clears the pending timer, so a superseded/dismissed toast never fires a late
  // dismiss.
  signal.addEventListener("abort", () => clearTimeout(timer));

  // Two independent pause sources are tracked so releasing one (e.g. the mouse) doesn't
  // dismiss while the other still holds (e.g. focus is on "Undo").
  const onPointerEnter = () => {
    hovering = true;
    clearTimeout(timer);
  };
  const onPointerLeave = () => {
    hovering = false;
    resume();
  };
  const onFocusIn = () => {
    focusWithin = true;
    clearTimeout(timer);
  };
  const onFocusOut = ( event) => {
    // `focusout` also fires when moving BETWEEN the toast's own buttons; only treat it as
    // "left" when focus actually landed outside the toast.
    const next =  (event.relatedTarget);
    if (next && toast.contains(next)) {
      return;
    }
    focusWithin = false;
    resume();
  };
  toast.addEventListener("pointerenter", onPointerEnter, listen);
  toast.addEventListener("pointerleave", onPointerLeave, listen);
  toast.addEventListener("focusin", onFocusIn, listen);
  toast.addEventListener("focusout", onFocusOut, listen);

  start();
}

// Teardown for the CURRENT toast: a single `abort()` clears the timer, removes every
// listener, and pulls the node (all wired as `abort` handlers). Called before showing the
// next toast (replace semantics). Idempotent, so a stale reference can't disturb a newer
// toast.
let activeTeardown = null;

// Show a toast. Any currently-visible toast is replaced.
export function showToast({ message, actionLabel, onAction, duration = DEFAULT_DURATION_MS }) {
  const region = toastRegion();
  // Replace semantics: end the outgoing toast (timer, listeners, and its node).
  activeTeardown?.();
  // Resync the region to THIS call. The outgoing toast normally removes itself via its
  // abort listener, but a reload/HMR can leave `activeTeardown` desynced from the DOM (the
  // region is a persistent <body> node), so clear defensively.
  region.replaceChildren();

  // Per-toast controller: teardown is a single `abort()`, with nothing leaking across
  // toasts (listeners register with its signal; the timer and node removal are `abort`
  // handlers).
  const controller = new AbortController();
  const { signal } = controller;
  const listen = { signal };
  const dismiss = () => controller.abort();
  activeTeardown = dismiss;

  const toast = buildToast({ message, actionLabel, onAction, dismiss }, listen);
  signal.addEventListener("abort", () => toast.remove());
  region.append(toast);

  if (isAutoDismiss(duration)) {
    installAutoDismiss(toast, duration, dismiss, signal);
  }

  return dismiss;
}

// A non-positive or non-finite duration means STICKY: no timer is armed, so the toast
// waits for an explicit dismissal (the PWA update prompt relies on this via `duration: 0`).
function isAutoDismiss(duration) {
  return duration > 0 && Number.isFinite(duration);
}
